import { database } from "./database";
import { Collections } from "@/types/Collections.ts";
import {
  BaseFirestore,
  CreateGeneratorDto,
  Generator,
  GeneratorIndex,
  PaginationOptions,
  TypeSenseGenerator,
} from "@/models";
import { firestoreToDateTime } from "@/lib/utils.ts";
import { config } from "@/lib/config";
import { SortingState } from "@tanstack/react-table";
import { SearchParams } from "typesense/lib/Typesense/Documents";
import { typesense } from "@/services/typesense";

export const generator = {
  get: async (id: string, tenantId: string) => {
    const g = await database.get<Generator>(
      id,
      `${Collections.tenants}/${tenantId}/${Collections.generators}`
    );
    return {
      ...g,
      batteryChangeDate: firestoreToDateTime(g.batteryChangeDate),
      installedDate: firestoreToDateTime(g.installedDate),
      nextServiceDate: firestoreToDateTime(g.nextServiceDate),
      lastServiceDate: firestoreToDateTime(g.lastServiceDate),
      renewEndDate: firestoreToDateTime(g.renewEndDate),
      renewStartDate: firestoreToDateTime(g.renewStartDate),
      warrantyExpirationDate: firestoreToDateTime(g.warrantyExpirationDate),
    } as Generator;
  },
  create: async (generatorData: CreateGeneratorDto, tenantId: string) => {
    const newGenerator: Omit<Generator, keyof BaseFirestore> = {
      ...generatorData,
    };
    return database.create<Generator>(
      newGenerator,
      `${Collections.tenants}/${tenantId}/${Collections.generators}`
    );
  },
  update: async (
    generatorId: string,
    data: Partial<Generator>,
    tenantId: string
  ) => {
    return database.update<Generator>(
      { id: generatorId, ...data },
      `${Collections.tenants}/${tenantId}/${Collections.generators}/${generatorId}`
    );
  },
  getAll: async (tenantId: string) => {
    return database.list(
      `${Collections.tenants}/${tenantId}/${Collections.generators}`
    );
  },
  list: async (
    tenantId: string,
    paginationOptions: PaginationOptions<Generator>
  ) => {
    return database.paginate(
      `${Collections.tenants}/${tenantId}/${Collections.generators}`,
      {
        orderBy: "createdAt",
        ...paginationOptions,
      }
    );
  },
  subscribe: (
    generatorId: string,
    callback: (doc: Generator | undefined) => void,
    tenantId: string
  ) => {
    return database.subscribeDocument<Generator>(
      generatorId,
      `${Collections.tenants}/${tenantId}/${Collections.generators}`,
      (doc: Generator | undefined) =>
        doc
          ? callback({
              ...doc,
              batteryChangeDate:
                firestoreToDateTime(doc.batteryChangeDate) ?? null,
              installedDate: firestoreToDateTime(doc.installedDate) ?? null,
              nextServiceDate: firestoreToDateTime(doc.nextServiceDate) ?? null,
              lastServiceDate: firestoreToDateTime(doc.lastServiceDate) ?? null,
              renewEndDate: firestoreToDateTime(doc.renewEndDate) ?? null,
              renewStartDate: firestoreToDateTime(doc.renewStartDate) ?? null,
              warrantyExpirationDate:
                firestoreToDateTime(doc.warrantyExpirationDate) ?? null,
            })
          : undefined
    );
  },
  getOptions: async (tenantId: string) => {
    return database.get<GeneratorIndex>(
      "index",
      `${Collections.tenants}/${tenantId}/${Collections.generators}`
    );
  },
  convertTypesenseToGenerator: (doc: TypeSenseGenerator): Generator => {
    return {
      tenantId: doc.tenantId,

      airFilter: doc.airFilter,
      battery: doc.battery,
      batteryChangeDate: new Date(doc.batteryChangeDate * 1000),

      oilFilter: doc.oilFilter,
      customerId: doc.customerId,
      installedDate: new Date(doc.installedDate * 1000),
      notes: doc.notes,
      kwRating: doc.kwRating,
      maintenanceKit: doc.maintenanceKit,
      make: doc.make,
      modelNumber: doc.modelNumber,
      serialNumber: doc.serialNumber,
      mobileLinkExp: doc.mobileLinkExp,

      lastServiceDate: new Date(doc.lastServiceDate * 1000),
      nextServiceDate: new Date(doc.nextServiceDate * 1000),
      planType: doc.planType,
      renewStartDate: new Date(doc.renewStartDate * 1000),
      renewEndDate: new Date(doc.renewEndDate * 1000),
      serviceAgreementPrice: doc.serviceAgreementPrice,
      warranty: doc.warranty,
      warrantyExpirationDate: new Date(doc.warrantyExpirationDate * 1000),

      _deleted: doc._deleted,
      updatedAt: new Date(doc.updatedAt * 1000),
      createdAt: new Date(doc.createdAt * 1000),
      updatedBy: doc.updatedBy,
      createdBy: doc.createdBy,
      id: doc.id,
      _source: doc._source,
    };
  },
  search: async (
    tenantId: string,
    searchString: string,
    page: number = 1,
    perPage: number = 10,
    orderBy?: SortingState
  ): Promise<{ data: Generator[]; count: number; total: number }> => {
    const searchParameters: SearchParams = {
      q: searchString,
      query_by: "make,modelNumber,serialNumber",
      filter_by: `tenantId:=${tenantId}`,
      sort_by: orderBy
        ? orderBy
            .map((item) => `${item.id}:${item.desc ? "desc" : "asc"}`)
            .join(",")
        : "",
      infix: "fallback",
      page,
      per_page: perPage,
    };
    const hits = await typesense
      .collections<TypeSenseGenerator>(config.typesense.collections.generator)
      .documents()
      .search(searchParameters);
    return {
      total: hits.found ?? 0,
      count: hits.hits?.length ?? 0,
      data:
        hits.hits?.map((d) =>
          generator.convertTypesenseToGenerator(d.document)
        ) ?? [],
    };
  },
  generateRandom: async (tenantId: string) => {
    if (config.devMode) {
      const { faker } = await import("@faker-js/faker");

      const fakeData: CreateGeneratorDto = {
        airFilter: faker.vehicle.vin(),
        battery: faker.vehicle.vrm(),
        batteryChangeDate: faker.date.past(),
        oilFilter: faker.vehicle.vin(),
        tenantId: tenantId,
        customerId: "",
        installedDate: faker.date.past(),
        notes: faker.lorem.sentence(),
        kwRating: faker.number.int(),
        maintenanceKit: faker.commerce.productName(),
        make: faker.company.name(),
        modelNumber: faker.string.alphanumeric({ length: 5 }),
        serialNumber: faker.string.alphanumeric({ length: 12 }),
        mobileLinkExp: faker.date.future().toISOString(),
        lastServiceDate: faker.date.recent(),
        nextServiceDate: faker.date.future(),
        planType: faker.lorem.word(),
        renewStartDate: faker.date.future(),
        renewEndDate: faker.date.future(),
        serviceAgreementPrice: Number(faker.commerce.price()),
        warranty: faker.lorem.word(),
        warrantyExpirationDate: faker.date.future(),
      };

      return await generator.create(fakeData, tenantId);
    }
  },
};
